import { c as _c } from "react/compiler-runtime";
import { formatCents } from 'betterplace-utils';
import classNames from 'classnames';
import { DonationFeedItem, TextLink, VirtualScrollItemStyle } from 'design-system/client';
import type { FeedDonation } from 'indexed-db';
import { useTranslations, useLocale } from 'next-intl';
import { type ForwardedRef, forwardRef, memo, useMemo } from 'react';
import HiddenAmount from '../HiddenAmount';
import styles from './DonationCard.module.css';
import { useRelativeTimeFromTimestamp } from '@/helpers/hooks';
export type DonationCardDonation = Pick<FeedDonation, 'amountInCents' | 'showAmount' | 'displayName' | 'confirmedAt' | 'message' | 'id'> & {
  markedAsSeen?: boolean;
};
export type DonationCardProps = {
  donation?: DonationCardDonation;
  style?: VirtualScrollItemStyle;
  areAllDonationsUnseen?: boolean;
  cta?: false;
  donationFormUrl?: string;
  stableKey?: number;
  mayStartAnimation?: boolean;
} | {
  donation?: DonationCardDonation;
  style?: VirtualScrollItemStyle;
  areAllDonationsUnseen?: boolean;
  cta: true;
  donationFormUrl: string;
  stableKey?: number;
  mayStartAnimation?: boolean;
};
function Amount(t0) {
  const $ = _c(5);
  const {
    donation
  } = t0;
  const locale = useLocale();
  let t1;
  if ($[0] !== donation || $[1] !== locale) {
    t1 = donation?.showAmount ? formatCents(locale, donation.amountInCents) : <HiddenAmount />;
    $[0] = donation;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const amount = t1;
  let t2;
  if ($[3] !== amount) {
    t2 = <>{amount}</>;
    $[3] = amount;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Details(t0) {
  const $ = _c(18);
  const {
    donation
  } = t0;
  const t = useTranslations("nextjs");
  let t1;
  if ($[0] !== t) {
    t1 = t("donation_list.by");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== donation.displayName || $[3] !== t) {
    t2 = donation.displayName ?? t("donation_list.anonymous_user");
    $[2] = donation.displayName;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span>{t2}</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <>{t1}{t3}</>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const header = t4;
  const timestamp = useRelativeTimeFromTimestamp(donation.confirmedAt ?? "");
  let t5;
  if ($[10] !== donation.message || $[11] !== t) {
    t5 = donation.message ? t("core.in_quotation_marks", {
      quote: donation.message
    }) : undefined;
    $[10] = donation.message;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const comment = t5;
  const id = donation.id;
  let t6;
  if ($[13] !== comment || $[14] !== header || $[15] !== id || $[16] !== timestamp) {
    t6 = <DonationFeedItem.SecondSlot.DetailsContainer header={header} timestamp={timestamp} comment={comment} id={id} />;
    $[13] = comment;
    $[14] = header;
    $[15] = id;
    $[16] = timestamp;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
const DonationCardInner = forwardRef(function DonationCardInner({
  donation,
  style,
  stableKey,
  mayStartAnimation
}: DonationCardProps, ref: ForwardedRef<HTMLDivElement>) {
  const seen = !donation || !!donation.markedAsSeen;
  const showAnimation = mayStartAnimation && !seen;
  return <DonationFeedItem key={stableKey} ref={ref} style={style} className={classNames({
    [styles.newDonation!]: showAnimation
  })}>
      <DonationFeedItem.FirstSlot>
        {donation ? <DonationFeedItem.FirstSlot.AmountContainer amount={<Amount donation={donation} />} /> : <DonationFeedItem.FirstSlot.AmountContainer skeleton />}
      </DonationFeedItem.FirstSlot>
      <DonationFeedItem.SecondSlot>
        {donation ? <Details donation={donation} /> : <DonationFeedItem.SecondSlot.DetailsContainer id="" skeleton />}
      </DonationFeedItem.SecondSlot>
    </DonationFeedItem>;
});
const CtaCard = forwardRef(function CtaCard(t0, ref) {
  const $ = _c(9);
  const {
    donationFormUrl
  } = t0;
  const t = useTranslations("nextjs.donation_feed");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DonationFeedItem.FirstSlot><DonationFeedItem.FirstSlot.AmountContainer amount={<Amount />} /></DonationFeedItem.FirstSlot>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("be_the_first");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== donationFormUrl || $[4] !== t2) {
    t3 = <DonationFeedItem.SecondSlot><DonationFeedItem.SecondSlot.DetailsContainer id="" cta={<TextLink href={donationFormUrl}>{t2}</TextLink>} /></DonationFeedItem.SecondSlot>;
    $[3] = donationFormUrl;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== ref || $[7] !== t3) {
    t4 = <DonationFeedItem ref={ref}>{t1}{t3}</DonationFeedItem>;
    $[6] = ref;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
});
const DonationCard = forwardRef(function DonationCard(props, ref) {
  const $ = _c(10);
  let donationFormUrl;
  let props_;
  let t0;
  if ($[0] !== props) {
    ({
      cta: t0,
      donationFormUrl,
      ...props_
    } = props);
    $[0] = props;
    $[1] = donationFormUrl;
    $[2] = props_;
    $[3] = t0;
  } else {
    donationFormUrl = $[1];
    props_ = $[2];
    t0 = $[3];
  }
  const cta = t0 === undefined ? false : t0;
  if (cta) {
    let t1;
    if ($[4] !== donationFormUrl || $[5] !== ref) {
      t1 = <CtaCard donationFormUrl={donationFormUrl} ref={ref} />;
      $[4] = donationFormUrl;
      $[5] = ref;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  let t1;
  if ($[7] !== props_ || $[8] !== ref) {
    t1 = <DonationCardInner {...props_} ref={ref} />;
    $[7] = props_;
    $[8] = ref;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
});
export default memo(DonationCard);